import { useState, useContext } from "react";
import { ITreeItem } from "@/interfaces";
import {
  ComponentsContext,
  FoldersContext,
  IComponentsContext,
} from "@/contexts";
import { DEFAULT_FOLDER_KEY } from "@/constants";
import { getTranslation, redirect } from "@/utils";
import { ConfirmationModal, TextInputModal } from "@/shared-components";
import {
  MuxPencil,
  MuxPlusCircle,
  MuxPencilRename,
  MuxSetting,
  MuxTrash,
} from "@/Icons";
import { useFavoritesContext } from "@/contexts";
import { FavoriteContextMenuItem } from "../../../FiltersPanel/components/FilterContextMenu/components";
import { findNodeById } from "@/utils/common";
import { TreeService } from "@/services";

interface FilterActionsMenuProps {
  items: ITreeItem[];
  data: ITreeItem;
  hasEditPermissions: boolean;
  setIsEditable: (isRename: boolean) => void;
}

export const FolderContextMenu = ({
  data,
  items,
  hasEditPermissions,
  setIsEditable,
}: FilterActionsMenuProps) => {
  const { DropdownMenu, DropdownMenuBody, DropdownMenuItem } =
    useContext<IComponentsContext>(ComponentsContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const { id } = data;
  const { scope, deleteFolder, addSubItem, reloadItems } =
    useContext(FoldersContext)!;
  const { projectId } = scope;
  const { favorites, reloadFavorites } = useFavoritesContext();
  const isFavorite = favorites.some((f) => f.id.toString().includes(id));

  async function handleAddSubItem(inputValue: string) {
    setShowEditModal(false);
    const node = findNodeById(items, id);
    if (!node) return;
    const newFolder: void | ITreeItem = await addSubItem(id, inputValue).then(
      (folder) => folder,
    );
    if (newFolder) {
      node.children.push(newFolder);
      node.serverObject.has_children = true;
      node.extra.paddingLeft = TreeService.getPaddingLeft(node.serverObject);
      reloadItems(items);
    }
  }

  async function handleDeletion() {
    try {
      mixpanel.track("folder-tree", {
        Action: "delete-folder",
      });
      setShowEditModal(false);
      await deleteFolder(id);
      TreeService.deleteTreeItemFromState(items, data);
      reloadItems(items);

      await reloadFavorites();
    } catch (e) {
      //TODO: show a toaster
      console.error(`handleDeletion->`, e);
    }
  }

  if (id === DEFAULT_FOLDER_KEY) {
    return null;
  }

  return (
    <DropdownMenu
      // Workaround for stupid hover logic
      overlayProps={{
        offset: -10,
        placement: "bottom-end",
        flip: true,
        // Cap height to the available space so the menu scrolls instead of being cut off
        "max-size?": true,
      }}
      toggleElement={({ toggle }: { toggle: () => void }) => {
        return (
          <span onClick={toggle} onKeyDown={toggle} role="button" tabIndex={0}>
            <MuxSetting />
          </span>
        );
      }}
    >
      <DropdownMenuBody>
        <DropdownMenuItem
          disabled={!hasEditPermissions}
          onClick={() => {
            mixpanel.track("folder-tree", {
              Action: "edit-folder",
            });
            redirect(`/p/${projectId}/folders/${id}/edit`);
          }}
          text={getTranslation("generic.button.edit", "Edit")}
          icon={<MuxPencil />}
        />

        <DropdownMenuItem
          onClick={() => {
            mixpanel.track("folder-tree", {
              Action: "rename-folder",
            });
            setIsEditable(true);
          }}
          text={getTranslation("filters-tree.context-menu.rename", "Rename")}
          icon={<MuxPencilRename />}
        />

        <FavoriteContextMenuItem isFavorite={isFavorite} data={data} />

        <DropdownMenuItem
          disabled={!hasEditPermissions}
          onClick={() => {
            mixpanel.track("folder-tree", {
              Action: "add-sub-folder",
            });
            setShowEditModal(true);
          }}
          icon={<MuxPlusCircle />}
          text={getTranslation(
            "filters-tree.context-menu.add-sub-folder",
            "Add Sub Folder",
          )}
        />

        <TextInputModal
          show={showEditModal}
          title={getTranslation(
            "filters-tree.context-menu.add-sub-folder",
            "Add Sub Folder",
          )}
          placeholder={getTranslation(
            "filters-tree.modal.add-sub-folder.placeholder",
            "New Folder Name",
          )}
          onYes={handleAddSubItem}
          onNo={() => setShowEditModal(false)}
          yesLabel={getTranslation("generic.button.save", "Save")}
          noLabel={getTranslation("generic.button.cancel", "Cancel")}
        />

        <DropdownMenuItem
          disabled={!hasEditPermissions}
          onClick={() => setShowDeleteModal(true)}
          icon={<MuxTrash />}
          text={getTranslation("generic.button.delete", "Delete")}
        />

        <ConfirmationModal
          show={showDeleteModal}
          title={getTranslation(
            "filters-tree.modal.delete-folder.title",
            "Delete Folder?",
          )}
          body={getTranslation(
            "filters-tree.modal.delete-folder.body",
            "The folder and its sub-folders will be deleted, but the data within will not be lost.",
          )}
          onYes={handleDeletion}
          onNo={() => setShowDeleteModal(false)}
          yesLabel={getTranslation("generic.button.delete", "Delete")}
          noLabel={getTranslation("generic.button.cancel", "Cancel")}
        />
      </DropdownMenuBody>
    </DropdownMenu>
  );
};
